import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  EdgeAssertion,
  EdgeAssertionSummary,
  PivotLens,
  isNodeKind,
  PivotFilters,
  PivotNeighborhood,
  PivotNodeKind,
  PivotSlice,
  SCALE_PROFILES,
  ScaleProfileKey,
} from '../models/pivot.models';
import {
  buildAggregate,
  buildCube,
  buildDegree,
  buildFacets,
  buildMembers,
  buildNeighborhood,
  getTenant,
  pickSeedEntity,
} from '../sample/pivot-edge-generator';

/**
 * HTTP interceptor for the AIM Pivot prototype.
 *
 * Serves the four net-new endpoints from `pivot-edge-generator.ts`. The
 * canonical entity endpoints are NOT intercepted — those keep hitting the real
 * inventory API, so the prototype runs against real tenant cardinality and only
 * the edges are synthetic.
 *
 * Delays are varied (per PROTOTYPE_RULES §2) so independent widget loading is
 * visible during dev: the cube face returns before a large neighbourhood does,
 * which is also the ordering the real backend will produce.
 *
 * Disable by removing from the withInterceptors([...]) array in app.config.ts.
 */

/**
 * Human rulings on relationships, keyed `source->target`.
 *
 * Session-scoped here; the real thing is a table beside `ResolutionRule`. The
 * important property either way is PERMANENCE — a denied edge must stay denied
 * across rebuilds, or the resolver re-proposes next week what the analyst
 * rejected today and the control is worse than not offering it.
 */
const edgeAssertions = new Map<string, EdgeAssertionSummary>();

function assertionKey(source: string, target: string): string {
  return `${source}->${target}`;
}

/**
 * Apply stored rulings to a freshly built neighbourhood.
 *
 * Done here rather than inside the generator so the generator stays a pure
 * function of the tenant — which is exactly the split a real backend has, where
 * the resolver produces candidate edges and a join against the assertion table
 * overrides them.
 */
function applyAssertions(n: PivotNeighborhood): PivotNeighborhood {
  if (!edgeAssertions.size) return n;

  const denied = new Set<string>();
  const edges = n.edges
    .map((e) => {
      const a = edgeAssertions.get(assertionKey(e.source, e.target));
      if (!a) return e;
      if (a.action === 'deny') {
        denied.add(e.target);
        return null;
      }
      // Confidence is left untouched on purpose — the resolver's original score
      // is what future confirmations get scored against.
      return { ...e, provenance: 'confirmed' as const, assertion: a };
    })
    .filter((e): e is NonNullable<typeof e> => !!e);

  const nodes = n.nodes.filter((node) => !denied.has(node.node_id));

  // The relation index has to fall with the graph. It is what the group switcher
  // reads, so leaving a denied relationship counted there would offer a group
  // that no longer has anything in it.
  const deniedByKind = new Map<PivotNodeKind, number>();
  for (const node of n.nodes) {
    if (!denied.has(node.node_id)) continue;
    const kind = node.is_aggregate ? node.represents ?? 'aggregate' : node.kind;
    deniedByKind.set(kind, (deniedByKind.get(kind) ?? 0) + (node.is_aggregate ? node.weight : 1));
  }
  const relations = n.relations
    .map((r) => ({ ...r, total: Math.max(0, r.total - (deniedByKind.get(r.kind) ?? 0)) }))
    .filter((r) => r.total > 0);

  return {
    ...n,
    edges,
    nodes,
    relations,
    total_degree: Math.max(0, n.total_degree - denied.size),
  };
}

/**
 * Read the active filters off a request (ONE-3506).
 *
 * Accepts both wire forms because the two consumers cannot use the same one:
 * the neighbourhood URL repeats `tag=` per value, while `<app-datatable>`'s
 * `initialFilters` is a flat map and can only carry one string per key. The
 * joined form uses U+001F (unit separator) rather than a comma — tags are
 * customer-authored free text, and splitting user data on a character it is
 * allowed to contain is how a filter starts matching nothing for one customer
 * and nobody can reproduce it.
 *
 * Values are taken as-is and only ever compared for equality against the
 * tenant's own tag pool, so an unknown value simply matches nothing.
 */
function readFilters(params: URLSearchParams): PivotFilters {
  const read = (key: string): string[] =>
    params
      .getAll(key)
      .flatMap((v) => v.split('\u001f'))
      .map((v) => v.trim())
      .filter(Boolean);
  return { tags: read('tag'), classifications: read('classification') };
}

function respond<T>(body: T, ms: number) {
  return of(new HttpResponse<T>({ status: 200, body })).pipe(delay(ms));
}

function parseQueryString(url: string): URLSearchParams {
  const idx = url.indexOf('?');
  return new URLSearchParams(idx >= 0 ? url.substring(idx + 1) : '');
}

/**
 * Latency that grows with tenant size, so the shape selector exercises the
 * loading states too. A 21,522-user tenant should not feel like a 230-user one.
 *
 * Keyed off the profile's own totals rather than the key name, so re-pointing a
 * profile at a different org changes the felt latency with it.
 */
function scaledDelay(base: number, scale: ScaleProfileKey): number {
  const p = SCALE_PROFILES[scale];
  const size = p.users + p.endpoints;
  const factor = size > 10_000 ? 2.2 : size > 2_000 ? 1.3 : 1;
  return Math.round(base * factor);
}

export const pivotMockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('/v1/inventory/pivot/')) {
    return next(req);
  }

  const params = parseQueryString(req.url);
  const scale = (params.get('scale') ?? 'balanced') as ScaleProfileKey;
  const tenant = getTenant(scale);

  console.log('[Pivot Mock Interceptor]', req.method, req.url);

  // Assertions — a human confirming or denying one relationship.
  if (req.method === 'POST' && req.url.includes('/v1/inventory/pivot/assertions/')) {
    const body = req.body as EdgeAssertion;
    const summary: EdgeAssertionSummary = {
      action: body.action,
      asserted_by: 'You',
      asserted_at: new Date().toISOString(),
      reason: body.reason ?? null,
    };
    edgeAssertions.set(assertionKey(body.source, body.target), summary);
    return respond(summary, 160);
  }

  // Seed — a representative entity of a kind to land on.
  if (req.method === 'GET' && req.url.includes('/v1/inventory/pivot/seed/')) {
    const kind = (params.get('kind') ?? 'user') as PivotNodeKind;
    // A representative entity of the vertical — the one with edges worth
    // showing. There is no longer a mode that lands you somewhere else: the
    // `threat` lens used to seed onto the tenant's worst entity, which fought
    // every entry point that names the entity to open.
    const id = pickSeedEntity(tenant, kind);
    return respond({ kind, id }, 90);
  }

  // Neighborhood — one entity's rings.
  if (req.method === 'GET' && req.url.includes('/v1/inventory/pivot/neighborhood/')) {
    const kind = (params.get('kind') ?? 'user') as PivotNodeKind;
    const id = Number(params.get('id') ?? '1');
    // Validated against the known kinds rather than cast: `group` reaches here
    // from a URL, and an unrecognised value would narrow the response to nothing
    // and render as an entity with no relationships at all — which is a
    // meaningful finding in this product, and would be a lie.
    const rawGroup = params.get('group');
    const group = isNodeKind(rawGroup) ? rawGroup : null;
    const body = applyAssertions(buildNeighborhood(tenant, kind, id, group, readFilters(params)));
    // See PivotService.getNeighborhood — the caller may know the entity's real
    // name when the generator only knows a synthetic one. Overriding here rather
    // than in a component keeps every renderer reading one label off one
    // response.
    const suppliedLabel = params.get('label');
    if (suppliedLabel) {
      body.focus = { ...body.focus, label: suppliedLabel };
    }
    return respond(body, scaledDelay(260, scale));
  }

  // Degree — how far one node reaches, without drawing its neighbourhood.
  //
  // `buildDegree` was written and imported with the others but never routed, so
  // every caller fell through to the live API and took a 404. The case panel's
  // blast-radius headline is the first consumer; deriving it from
  // `buildNeighborhood` like the generator does is what keeps that number equal
  // to the one the Visualizer shows for the same entity — two different counts
  // for one asset would be worse than showing none.
  if (req.method === 'GET' && req.url.includes('/v1/inventory/pivot/degree/')) {
    const kind = isNodeKind(params.get('kind')) ? (params.get('kind') as PivotNodeKind) : 'user';
    const id = Number(params.get('id') ?? '1');
    return respond(buildDegree(tenant, kind, Number.isFinite(id) ? id : 1), scaledDelay(90, scale));
  }

  // Facets — the tags and classifications worth filtering by, with counts.
  if (req.method === 'GET' && req.url.includes('/v1/inventory/pivot/facets/')) {
    const kind = isNodeKind(params.get('kind')) ? (params.get('kind') as PivotNodeKind) : 'user';
    const id = Number(params.get('id') ?? '1');
    return respond(buildFacets(tenant, kind, Number.isFinite(id) ? id : 1), scaledDelay(120, scale));
  }

  // Members — OData-paged rows behind a hub or a cube cell.
  if (req.method === 'GET' && req.url.includes('/v1/inventory/pivot/members/')) {
    const skip = Number(params.get('$skip') ?? '0');
    const top = Number(params.get('$top') ?? '25');
    const search = (params.get('$search') ?? '').replace(/^'(.*)'$/, '$1').toLowerCase();

    const { count, rows } = buildMembers(tenant, params, skip, top, search, readFilters(params));
    return respond({ '@odata.count': count, value: rows }, scaledDelay(220, scale));
  }

  return next(req);
};
